import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '@/lib/db';
import type { PostEnriched, TickerScore } from '@/lib/db/types';

export interface UseTodaysScoresResult {
  scores: TickerScore[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTodaysScores(): UseTodaysScoresResult {
  const [scores, setScores] = useState<TickerScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const aliveRef = useRef(true);

  const fetchScores = useCallback(async () => {
    try {
      const [roundScores, posts] = await Promise.all([
        db.rounds.todaysScores(),
        db.posts.list({ limit: 80, includeCourseHoles: false }),
      ]);
      const data = mergeTickerScores(roundScores, posts);
      if (!aliveRef.current) return;
      setScores(data);
      setError(null);
    } catch (e) {
      if (!aliveRef.current) return;
      setError((e as Error).message);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchScores();

    const unsubscribe = db.rounds.subscribeNew(() => {
      fetchScores();
    });

    const interval = window.setInterval(fetchScores, 60_000);

    return () => {
      aliveRef.current = false;
      unsubscribe();
      window.clearInterval(interval);
    };
  }, [fetchScores]);

  return { scores, loading, error, refresh: fetchScores };
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

function mergeTickerScores(base: TickerScore[], posts: PostEnriched[]): TickerScore[] {
  const out = [...base];
  const seen = new Set(base.map((s) => `${s.member_id}:${s.played_at}:${s.gross_total}`));

  posts.forEach((p) => {
    if (p.type !== 'round' || !p.round || !isToday(p.round.played_at)) return;
    const groupScores = p.metadata?.groupScores || [];
    groupScores.forEach((g, idx) => {
      const gross = (g.scores || []).reduce((sum, h) => sum + (h.strokes || 0), 0);
      if (!gross) return;
      const memberId = g.id || `guest-${idx}`;
      const key = `${memberId}:${p.round!.played_at}:${gross}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        id: `${p.id}-${memberId}`,
        member_id: memberId,
        member_name: g.name || 'Guest',
        course_id: p.round!.course_id,
        course_slug: p.round!.course?.slug || '',
        course_name: p.round!.course?.name || p.metadata?.courseName || '',
        gross_total: gross,
        net_total: null,
        holes: p.round!.holes,
        played_at: p.round!.played_at,
        created_at: p.created_at,
      });
    });
  });

  return out.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}
